import React, { useState, useRef } from "react";
import { Typography, Box, IconButton, LinearProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
// import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";

// Define allowed file types
const ALLOWED_FILE_TYPES = [
  "application/vnd.ms-excel", // สำหรับ .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // สำหรับ .xlsx
];

// Styles using makeStyles from MUI
const useStyles = makeStyles((theme) => ({
  dropzone: {
    border: "2px dashed #ccc", // Default border style
    textAlign: "center",
    cursor: "pointer",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "#fff",
    transition: "background-color 0.3s, border-color 0.3s, border-style 0.3s", // Transition effect for smooth changes
    "&:hover": {
      borderColor: "#2196F3", // Border color on hover
      backgroundColor: "#2196F314", // Background color on hover
    },
    height: "150px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  dropzoneError: {
    border: "2px solid #D32F2F", // Red border for error state
    backgroundColor: "#D32F2F0A", // Light red background for error state
    transition: "background-color 0.3s, border-color 0.3s, border-style 0.3s", // Transition effect for smooth changes
    "&:hover": {
      borderColor: "#2196F3", // Border color on hover
      backgroundColor: "#2196F314", // Background color on hover
    },
  },
  fileItem: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ccc",
    backgroundColor: "#fff",
  },
  fileName: {
    flexGrow: 1,
    paddingLeft: theme.spacing(1),
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
  fileIcon: {
    marginRight: theme.spacing(1),
    color: "#2196F3",
  },
  uploadIcon: {
    marginBottom: theme.spacing(1),
    color: "#2196F3",
  },
}));

const Dropzone = ({ files, setFiles, setError, error }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [isError, setIsError] = useState(false);

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const newFiles = Array.from(event.dataTransfer.files);

    const validFiles = newFiles.filter((file) => {
      // ตรวจสอบก่อนว่า file.type มีค่า หรือ fallback ไปเช็คจาก extension
      if (file.type) {
        return ALLOWED_FILE_TYPES.includes(file.type);
      } else if (file.name) {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        return ["xls", "xlsx"].includes(fileExtension);
      }
      return false; // กรณีที่ file.type และ file.name ไม่มีค่า
    });

    if (validFiles.length !== newFiles.length) {
      setError("Only .xls or .xlsx files are allowed.");
      setIsError(true);
    } else {
      setError(null);
      setIsError(false);
      handleFiles(validFiles);
    }
  };

  const handleInputChange = (event) => {
    const newFiles = Array.from(event.target.files);

    const validFiles = newFiles.filter((file) => {
      // ตรวจสอบก่อนว่า file.type มีค่า หรือ fallback ไปเช็คจาก extension
      if (file.type) {
        return ALLOWED_FILE_TYPES.includes(file.type);
      } else if (file.name) {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        return ["xls", "xlsx"].includes(fileExtension);
      }
      return false; // กรณีที่ file.type และ file.name ไม่มีค่า
    });

    if (validFiles.length !== newFiles.length) {
      setError("Only .xls or .xlsx files are allowed.");
      setIsError(true);
    } else {
      setError(null);
      setIsError(false);
      handleFiles(validFiles);
    }
  };

  const handleFiles = (newFiles) => {
    const updatedFiles = [...files];
    let hasError = false;

    newFiles.forEach((file) => {
      if (file.size <= 3 * 1024 * 1024) {
        updatedFiles.push({ file, status: "loading", progress: 0 });
        simulateUpload(file, updatedFiles.length - 1);
      } else {
        setError("File too large");
        hasError = true;
      }
    });

    if (!hasError) {
      setError(null);
    }

    setFiles(updatedFiles);
  };

  // Simulate file upload progress
  const simulateUpload = (file, index) => {
    const interval = setInterval(() => {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        if (updatedFiles[index].progress < 100) {
          updatedFiles[index].progress += 10;
        } else {
          updatedFiles[index].status = "complete";
          clearInterval(interval);
        }
        return updatedFiles;
      });
    }, 200);
  };

  // Handle deletion of uploaded file
  const handleDelete = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <>
      {/* Dropzone area for file upload */}
      <Box
        className={`${classes.dropzone} ${
          isError ? classes.dropzoneError : ""
        }`} // Conditional class based on isError state
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current.click()}
      >
        {/* <CloudUploadIcon className={classes.uploadIcon} fontSize="large" /> */}
        <UploadFileIcon className={classes.uploadIcon} fontSize="large" />
        <Typography variant="body2">
          <span style={{ textDecoration: "underline", color: "#2196F3" }}>
            Click to upload
          </span>{" "}
          or drag and drop
        </Typography>

        <Typography variant="body2" color="textSecondary">
          XlS or XLSX (max.10 MB)
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleInputChange}
          multiple
          accept=".xls,.xlsx"
        />
      </Box>

      {/* Display uploaded files */}
      {files?.map((fileObj, index) => (
        <Box key={index} className={classes.fileItem}>
          <UploadFileIcon className={classes.fileIcon} />
          <Typography variant="body2" className={classes.fileName}>
            {fileObj.file.name}
            <Box sx={{ width: "100%", mr: 1 }}>
              {formatFileSize(fileObj.file.size)}
              {fileObj.status === "failed" && (
                <Typography className={classes.errorMessage}>Failed</Typography>
              )}
              {fileObj.status === "complete" && " • Complete"}
              {fileObj.status === "loading" && " • Loading"}
            </Box>
            <Box sx={{ width: "40%", mr: 1 }}>
              {fileObj.status === "loading" && (
                <LinearProgress
                  variant="determinate"
                  value={fileObj.progress}
                  sx={{
                    borderRadius: 10,
                  }}
                />
              )}
            </Box>
          </Typography>
          {fileObj.status === "complete" && (
            <IconButton
              size="small"
              onClick={() => handleDelete(index)}
              color="secondary"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ))}
    </>
  );
};

export default Dropzone;
